/**
 * Pure functions to transform project export JSON into markdown files.
 * Story 464: Export full project data as a downloadable zip of markdown files
 *
 * Entry point: generateExportFiles(data) → Map<filepath, content>
 */

/** Matches ProjectExportData from workers/taskmgr/src/api/export.ts */
export interface ProjectExportData {
  project: {
    slug: string;
    name: string | null;
    exported_at: string;
  };
  epics: Array<{
    slug: string;
    title: string;
    description: string | null;
    story_count: number;
  }>;
  stories: Array<{
    external_id: string;
    title: string;
    status: string;
    priority: number;
    epic_slug: string | null;
    assignee: string | null;
    created_at: string;
    updated_at: string;
    documents: Array<{
      doc_type: string;
      summary: string | null;
      sections: Array<{
        section_key: string;
        title: string;
        body: string;
        anchor: string | null;
        seq: number;
        section_type: string | null;
        /** Story 553: requirement anchors this section covers. */
        covers: string[];
        /** Story 553: test types a design capability requires. */
        required_tests: string[];
      }>;
    }>;
    tasks: Array<{
      seq: number;
      title: string;
      body: string | null;
      status: string;
      task_type: string | null;
      assignee: string | null;
      implements: string[] | null;
      evidence: Record<string, unknown> | null;
      test_results: Record<string, unknown> | null;
    }>;
    comments: Array<{
      body: string;
      author: string | null;
      created_at: string;
    }>;
    learnings: Array<{
      learning_type: string;
      content: string;
      severity: string;
      created_at: string;
    }>;
    /** Story 553: matches StoryTraceability in workers/taskmgr/src/api/export-traceability.ts */
    traceability: {
      has_prd: boolean;
      has_design: boolean;
      requirements: Array<{ anchor: string; title: string; capabilities: string[] }>;
      capabilities: Array<{
        anchor: string;
        title: string;
        covers: string[];
        build_tasks: Array<{ seq: number; status: string }>;
        test_tasks: Array<{ seq: number; type: string; status: string }>;
        required_tests: string[];
        provided_tests: string[];
      }>;
      gaps: {
        requirement_unaddressed: string[];
        capability_without_build_task: string[];
        capability_missing_tests: Array<{ anchor: string; missing: string[] }>;
        capability_names_unknown_requirement: Array<{ anchor: string; unknown: string }>;
        task_names_unknown_capability: Array<{ seq: number; unknown: string }>;
        unreadable: Array<{ where: string; what: string }>;
      };
    };
  }>;
}

const EXTERNAL_ID_PAD_WIDTH = 3;
const MAX_SLUG_LENGTH = 50;

// Story 553: wording of the epic position and the story traceability section.
const EPIC_NO_STORIES = 'No stories in this epic.';
const TRACE_HEADING = '## Traceability';
const TRACE_NO_PRD = 'This story has no requirements document.';
const TRACE_NO_DESIGN = 'This story has no design document.';
const TRACE_NONE = 'none';
const TRACE_NO_GAPS = 'No gaps found.';
const LIST_SEPARATOR = ', ';

/**
 * Pad external ID with leading zeros for filesystem sorting.
 * e.g., "1" → "001", "42" → "042"
 */
export function padExternalId(id: string): string {
  return id.padStart(EXTERNAL_ID_PAD_WIDTH, '0');
}

/**
 * Convert a title to a filesystem-safe slug.
 * Lowercase, replace non-alphanumeric with hyphens, collapse hyphens, trim, truncate.
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_SLUG_LENGTH);
}

/**
 * Generate all export markdown files from project data.
 * Returns a Map where keys are file paths and values are markdown content.
 * Files are only generated when data exists (no empty files).
 */
export function generateExportFiles(data: ProjectExportData): Map<string, string> {
  const files = new Map<string, string>();

  files.set('README.md', generateProjectReadme(data));

  data.epics.forEach((epic, index) => {
    files.set(`epics/${epic.slug}.md`, generateEpicMarkdown(epic, data.stories, index + 1, data.epics.length));
  });

  for (const story of data.stories) {
    const prefix = `stories/${padExternalId(story.external_id)}-${slugify(story.title)}`;

    files.set(`${prefix}/story.md`, generateStoryMarkdown(story));

    for (const doc of story.documents) {
      const content = generateDocumentMarkdown(doc);
      if (content) {
        files.set(`${prefix}/${doc.doc_type}.md`, content);
      }
    }

    if (story.tasks.length > 0) {
      files.set(`${prefix}/tasks.md`, generateTasksMarkdown(story.tasks));
    }

    if (story.comments.length > 0) {
      files.set(`${prefix}/comments.md`, generateCommentsMarkdown(story.comments));
    }
  }

  // Aggregate learnings across all stories
  const allLearnings = data.stories.flatMap(s =>
    s.learnings.map(l => ({
      ...l,
      story_external_id: s.external_id,
      story_title: s.title,
    }))
  );
  if (allLearnings.length > 0) {
    files.set('learnings/learnings.md', generateLearningsMarkdown(allLearnings));
  }

  return files;
}

function generateProjectReadme(data: ProjectExportData): string {
  const { project, epics, stories } = data;
  const name = project.name || project.slug;

  const statusCounts = new Map<string, number>();
  for (const story of stories) {
    statusCounts.set(story.status, (statusCounts.get(story.status) || 0) + 1);
  }

  let md = `# ${name}\n\n`;
  md += `**Slug:** ${project.slug}\n`;
  md += `**Exported:** ${project.exported_at}\n\n`;

  // Stats
  md += `## Summary\n\n`;
  md += `| Metric | Count |\n`;
  md += `|--------|-------|\n`;
  md += `| Total stories | ${stories.length} |\n`;
  for (const [status, count] of statusCounts) {
    md += `| ${status} | ${count} |\n`;
  }
  md += `| Epics | ${epics.length} |\n`;
  md += `\n`;

  // Epic listing
  if (epics.length > 0) {
    md += `## Epics\n\n`;
    md += `| Epic | Stories |\n`;
    md += `|------|--------|\n`;
    for (const epic of epics) {
      md += `| ${epic.title} | ${epic.story_count} |\n`;
    }
    md += `\n`;
  }

  return md;
}

function generateEpicMarkdown(
  epic: ProjectExportData['epics'][0],
  stories: ProjectExportData['stories'],
  position: number,
  epicCount: number
): string {
  let md = `# ${epic.title}\n\n`;

  if (epic.description) {
    md += `${epic.description}\n\n`;
  }

  // Story 553: where this epic sits among all epics, in the web app's order.
  md += `**Position:** ${position} of ${epicCount}\n\n`;

  // Stories arrive in backlog order (priority, then creation), as the web app shows them.
  const epicStories = stories.filter(s => s.epic_slug === epic.slug);
  if (epicStories.length > 0) {
    md += `## Stories\n\n`;
    md += `| # | ID | Title | Status |\n`;
    md += `|---|----|-------|--------|\n`;
    epicStories.forEach((story, index) => {
      md += `| ${index + 1} | ${story.external_id} | ${story.title} | ${story.status} |\n`;
    });
    md += `\n`;
  } else {
    md += `${EPIC_NO_STORIES}\n\n`;
  }

  return md;
}

function generateStoryMarkdown(story: ProjectExportData['stories'][0]): string {
  let md = `# ${story.title}\n\n`;

  md += `| Field | Value |\n`;
  md += `|-------|-------|\n`;
  md += `| ID | ${story.external_id} |\n`;
  md += `| Status | ${story.status} |\n`;
  md += `| Priority | ${story.priority} |\n`;
  if (story.epic_slug) {
    md += `| Epic | ${story.epic_slug} |\n`;
  }
  if (story.assignee) {
    md += `| Assignee | ${story.assignee} |\n`;
  }
  md += `| Created | ${story.created_at} |\n`;
  md += `| Updated | ${story.updated_at} |\n`;
  md += `\n`;

  md += generateTraceabilityMarkdown(story.traceability);

  return md;
}

/**
 * Story 553: the story's requirement to capability to task chain and its gaps,
 * as the server decided them by the completion-gate rules. Rendering only.
 */
function generateTraceabilityMarkdown(trace: ProjectExportData['stories'][0]['traceability']): string {
  let md = `${TRACE_HEADING}\n\n`;

  if (!trace.has_prd) md += `${TRACE_NO_PRD}\n`;
  if (!trace.has_design) md += `${TRACE_NO_DESIGN}\n`;
  if (!trace.has_prd || !trace.has_design) md += `\n`;

  if (trace.has_prd || trace.has_design) {
    const list = (items: string[]) => (items.length > 0 ? items.join(LIST_SEPARATOR) : TRACE_NONE);

    md += `### Requirements\n\n`;
    md += `| Requirement | Title | Addressed by |\n`;
    md += `|-------------|-------|--------------|\n`;
    for (const r of trace.requirements) {
      md += `| ${r.anchor} | ${r.title} | ${list(r.capabilities)} |\n`;
    }
    md += `\n`;

    md += `### Capabilities\n\n`;
    md += `| Capability | Build tasks | Test tasks | Tests required | Tests present |\n`;
    md += `|------------|-------------|------------|----------------|---------------|\n`;
    for (const c of trace.capabilities) {
      const build = list(c.build_tasks.map((t) => `#${t.seq} (${t.status})`));
      const tests = list(c.test_tasks.map((t) => `#${t.seq} ${t.type} (${t.status})`));
      md += `| ${c.anchor} | ${build} | ${tests} | ${list(c.required_tests)} | ${list(c.provided_tests)} |\n`;
    }
    md += `\n`;
  }

  const g = trace.gaps;
  const lines = [
    ...g.requirement_unaddressed.map((a) => `Requirement ${a} is not addressed by any capability.`),
    ...g.capability_without_build_task.map((a) => `Capability ${a} has no build task.`),
    ...g.capability_missing_tests.map((c) => `Capability ${c.anchor} is missing ${c.missing.join(LIST_SEPARATOR)} tests.`),
    ...g.capability_names_unknown_requirement.map((c) => `Capability ${c.anchor} names requirement ${c.unknown}, which does not exist.`),
    ...g.task_names_unknown_capability.map((t) => `Task ${t.seq} names capability ${t.unknown}, which does not exist.`),
    ...g.unreadable.map((u) => `The ${u.what} of ${u.where} could not be read.`),
  ];
  md += `### Gaps\n\n`;
  md += lines.length > 0 ? lines.map((l) => `- ${l}`).join('\n') + '\n\n' : `${TRACE_NO_GAPS}\n\n`;

  return md;
}

function generateDocumentMarkdown(doc: ProjectExportData['stories'][0]['documents'][0]): string | null {
  if (doc.sections.length === 0 && !doc.summary) return null;

  const label = doc.doc_type === 'prd' ? 'PRD' : doc.doc_type === 'design' ? 'Technical Design' : doc.doc_type;
  let md = `# ${label}\n\n`;

  if (doc.summary) {
    md += `${doc.summary}\n\n`;
  }

  const sorted = [...doc.sections].sort((a, b) => a.seq - b.seq);
  for (const section of sorted) {
    md += `## ${section.title}\n\n`;
    if (section.anchor) {
      md += `> Anchor: \`${section.anchor}\`\n\n`;
    }
    md += `${section.body}\n\n`;
  }

  return md;
}

function generateTasksMarkdown(tasks: ProjectExportData['stories'][0]['tasks']): string {
  let md = `# Tasks\n\n`;

  md += `| # | Title | Status | Type | Implements |\n`;
  md += `|---|-------|--------|------|------------|\n`;
  for (const task of tasks) {
    const impl = task.implements ? task.implements.join(', ') : '';
    const type = task.task_type || '';
    md += `| ${task.seq} | ${task.title} | ${task.status} | ${type} | ${impl} |\n`;
  }
  md += `\n`;

  // Detailed task info for tasks with body or evidence
  const detailed = tasks.filter(t => t.body || t.evidence);
  if (detailed.length > 0) {
    md += `## Details\n\n`;
    for (const task of detailed) {
      md += `### ${task.seq}. ${task.title}\n\n`;
      if (task.body) {
        md += `${task.body}\n\n`;
      }
      if (task.evidence) {
        const rationale = (task.evidence as Record<string, unknown>).rationale;
        if (rationale) {
          md += `**Evidence:** ${rationale}\n\n`;
        }
      }
      if (task.test_results) {
        const tr = task.test_results as Record<string, unknown>;
        md += `**Tests:** ${tr.passed ?? 0} passed, ${tr.failed ?? 0} failed\n\n`;
      }
    }
  }

  return md;
}

function generateCommentsMarkdown(comments: ProjectExportData['stories'][0]['comments']): string {
  let md = `# Comments\n\n`;

  for (const comment of comments) {
    const author = comment.author || 'Unknown';
    md += `### ${author} — ${comment.created_at}\n\n`;
    md += `${comment.body}\n\n---\n\n`;
  }

  return md;
}

interface LearningWithStory {
  learning_type: string;
  content: string;
  severity: string;
  created_at: string;
  story_external_id: string;
  story_title: string;
}

function generateLearningsMarkdown(learnings: LearningWithStory[]): string {
  let md = `# Learnings\n\n`;

  // Group by story
  const byStory = new Map<string, LearningWithStory[]>();
  for (const l of learnings) {
    const key = l.story_external_id;
    const arr = byStory.get(key) || [];
    arr.push(l);
    byStory.set(key, arr);
  }

  for (const [storyId, storyLearnings] of byStory) {
    const title = storyLearnings[0].story_title;
    md += `## Story ${storyId}: ${title}\n\n`;

    for (const l of storyLearnings) {
      md += `- **[${l.learning_type}]** (${l.severity}) ${l.content}\n`;
    }
    md += `\n`;
  }

  return md;
}
